/**
 * GreenPopupNotification component for the SCAD system
 * Displays a green popup notification at the top of the screen
 */

import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { FaCheck, FaTimes } from "react-icons/fa";

const slideDown = keyframes`
  from {
    transform: translateY(-20px) translateX(-50%);
    opacity: 0;
  }
  to {
    transform: translateY(0) translateX(-50%);
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(0) translateX(-50%);
    opacity: 1;
  }
  to {
    transform: translateY(-20px) translateX(-50%);
    opacity: 0;
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 80px; /* Moved down to appear below the header */
  left: 50%;
  transform: translateX(-50%);
  background-color: #4caf50;
  color: white;
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  display: flex;
  align-items: center;
  min-width: 320px;
  max-width: 500px;
  width: fit-content;
  margin: 0 auto;
  animation: ${(props) => (props.exiting ? slideUp : slideDown)} 0.3s ease-out
    forwards;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const IconContainer = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.3);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  text-align: left;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 15px;
`;

const NotificationMessage = styled.div`
  font-weight: 400;
  font-size: 14px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  opacity: 0.7;
  cursor: pointer;
  margin-left: 8px;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const GreenPopupNotification = ({
  title = "Success",
  message,
  duration = 5000,
  onClose,
}) => {
  const [exiting, setExiting] = useState(false);

  const handleClose = useCallback(() => {
    setExiting(true);

    // Allow animation to complete before removing
    setTimeout(() => {
      if (onClose) {
        onClose();
      }
    }, 300);
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, handleClose]);

  return (
    <NotificationContainer exiting={exiting}>
      <IconContainer>
        <FaCheck />
      </IconContainer>
      <NotificationContent>
        <NotificationTitle>{title}</NotificationTitle>
        <NotificationMessage>{message}</NotificationMessage>
      </NotificationContent>
      <CloseButton onClick={handleClose} aria-label="Close notification">
        <FaTimes size={14} />
      </CloseButton>
    </NotificationContainer>
  );
};

export default GreenPopupNotification;
